import { useTranslation } from '@/hooks/use-translation';
import {
  Building2,
  CheckCircle2,
  Loader2,
  Mail,
  MessageSquare,
  Send,
  User,
} from 'lucide-react';
import { motion } from 'motion/react';
import { useState } from 'react';

interface FormData {
  name: string;
  email: string;
  organization: string;
  interest: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
}

interface InterestOption {
  value: string;
  label: string;
}

const WEB3FORMS_ACCESS_KEY = import.meta.env.VITE_WEB3FORMS_ACCESS_KEY;

export function ContactForm() {
  const { t, tArray } = useTranslation();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    organization: '',
    interest: '',
    message: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const interestOptions = tArray<InterestOption>('contactForm.form.interestOptions');

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('contactForm.validation.nameRequired');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('contactForm.validation.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('contactForm.validation.emailInvalid');
    }

    if (!formData.message.trim()) {
      newErrors.message = t('contactForm.validation.messageRequired');
    } else if (formData.message.trim().length < 10) {
      newErrors.message = t('contactForm.validation.messageMinLength');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          name: formData.name,
          email: formData.email,
          organization: formData.organization || 'Not provided',
          interest: formData.interest || 'Not selected',
          message: formData.message,
          subject: `New Contact Form Submission from ${formData.name}`,
          from_name: 'UrbanMaps Contact Form',
        }),
      });

      const result = await response.json();

      if (result.success) {
        setIsSubmitted(true);
      } else {
        setSubmitError(
          result.message || 'Failed to submit form. Please try again.',
        );
      }
    } catch {
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  if (isSubmitted) {
    return (
      <section
        id="contact-form"
        className="relative bg-slate-50 dark:bg-[#0d1318] py-16 lg:py-24 overflow-hidden"
      >
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern
                id="contact-grid"
                width="40"
                height="40"
                patternUnits="userSpaceOnUse"
              >
                <circle cx="20" cy="20" r="1" fill="#5a8a9a" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#contact-grid)" />
          </svg>
        </div>

        <div className="relative z-10 max-w-2xl mx-auto px-6">
          <motion.div
            className="text-center py-16"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <motion.div
              className="w-20 h-20 mx-auto mb-8 rounded-full bg-[#5a8a9a]/20 flex items-center justify-center"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            >
              <CheckCircle2 className="w-10 h-10 text-[#5a8a9a] dark:text-[#7ab8c9]" />
            </motion.div>
            <h3 className="font-display text-slate-900 dark:text-[#e8ebe9] text-3xl md:text-4xl mb-4">
              {t('contactForm.success.title')}
            </h3>
            <p className="text-slate-600 dark:text-[#8a9a9e] text-base md:text-lg leading-relaxed">
              {t('contactForm.success.description')}
            </p>
            <motion.button
              onClick={() => {
                setIsSubmitted(false);
                setFormData({
                  name: '',
                  email: '',
                  organization: '',
                  interest: '',
                  message: '',
                });
              }}
              className="mt-8 text-[#3d6a7a] dark:text-[#7ab8c9] text-sm md:text-base hover:text-[#2d5a6a] dark:hover:text-[#8ac8d9] transition-colors font-medium"
              whileHover={{ x: 5 }}
            >
              {t('contactForm.success.sendAnother')}
            </motion.button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="contact-form"
      className="relative bg-slate-50 dark:bg-[#0d1318] py-16 lg:py-24 overflow-hidden"
    >
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern
              id="contact-grid"
              width="40"
              height="40"
              patternUnits="userSpaceOnUse"
            >
              <circle cx="20" cy="20" r="1" fill="#5a8a9a" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#contact-grid)" />
        </svg>
      </div>

      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-64 h-64 opacity-20">
        <svg viewBox="0 0 200 200" className="w-full h-full">
          <path
            d="M0,100 Q50,50 100,100 T200,100"
            fill="none"
            stroke="#5a8a9a"
            strokeWidth="0.5"
          />
          <path
            d="M0,120 Q50,70 100,120 T200,120"
            fill="none"
            stroke="#5a8a9a"
            strokeWidth="0.5"
          />
          <path
            d="M0,140 Q50,90 100,140 T200,140"
            fill="none"
            stroke="#5a8a9a"
            strokeWidth="0.5"
          />
        </svg>
      </div>

      <div className="absolute bottom-0 right-0 w-64 h-64 opacity-20 rotate-180">
        <svg viewBox="0 0 200 200" className="w-full h-full">
          <path
            d="M0,100 Q50,50 100,100 T200,100"
            fill="none"
            stroke="#5a8a9a"
            strokeWidth="0.5"
          />
          <path
            d="M0,120 Q50,70 100,120 T200,120"
            fill="none"
            stroke="#5a8a9a"
            strokeWidth="0.5"
          />
          <path
            d="M0,140 Q50,90 100,140 T200,140"
            fill="none"
            stroke="#5a8a9a"
            strokeWidth="0.5"
          />
        </svg>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block mb-4 text-[#3d6a7a] dark:text-[#7ab8c9] text-sm md:text-base tracking-[0.2em] uppercase font-medium">
            {t('contactForm.label')}
          </span>
          <h2 className="font-display text-slate-900 dark:text-[#e8ebe9] text-4xl md:text-5xl lg:text-6xl leading-tight mb-6 tracking-tight">
            {t('contactForm.title')}
          </h2>
          <p className="text-slate-600 dark:text-[#8a9a9e] text-base md:text-lg max-w-xl mx-auto leading-relaxed">
            {t('contactForm.description')}
          </p>
        </motion.div>

        {/* Form */}
        <motion.form
          onSubmit={handleSubmit}
          className="max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="space-y-6">
            {/* Name & Email row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Name field */}
              <div className="group">
                <label
                  htmlFor="name"
                  className="block text-slate-600 dark:text-[#8a9a9e] text-sm md:text-base mb-2 font-medium"
                >
                  {t('contactForm.form.fullName')} <span className="text-[#3d6a7a] dark:text-[#7ab8c9]">*</span>
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 dark:text-[#4a5a5e] group-focus-within:text-[#5a8a9a] transition-colors">
                    <User className="w-4 h-4" />
                  </div>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t('contactForm.form.fullNamePlaceholder')}
                    className={`w-full bg-white dark:bg-[#0a0f14] border rounded-lg ${errors.name
                      ? 'border-red-500/50'
                      : 'border-slate-200 dark:border-[#1a2530]'
                      } text-slate-900 dark:text-[#e8ebe9] text-sm md:text-base pl-12 pr-4 py-4 focus:outline-none focus:border-[#3d6a7a] dark:focus:border-[#7ab8c9] focus:ring-2 focus:ring-[#3d6a7a]/10 dark:focus:ring-[#7ab8c9]/10 transition-all placeholder:text-slate-400 dark:placeholder:text-[#3a4a4e]`}
                  />
                </div>
                {errors.name && (
                  <motion.p
                    className="mt-2 text-red-500 dark:text-red-400 text-xs font-['DM_Sans']"
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {errors.name}
                  </motion.p>
                )}
              </div>

              {/* Email field */}
              <div className="group">
                <label
                  htmlFor="email"
                  className="block text-slate-600 dark:text-[#8a9a9e] text-sm md:text-base mb-2 font-medium"
                >
                  {t('contactForm.form.email')} <span className="text-[#3d6a7a] dark:text-[#7ab8c9]">*</span>
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 dark:text-[#4a5a5e] group-focus-within:text-[#5a8a9a] transition-colors">
                    <Mail className="w-4 h-4" />
                  </div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t('contactForm.form.emailPlaceholder')}
                    className={`w-full bg-white dark:bg-[#0a0f14] border rounded-lg ${errors.email
                      ? 'border-red-500/50'
                      : 'border-slate-200 dark:border-[#1a2530]'
                      } text-slate-900 dark:text-[#e8ebe9] text-sm md:text-base pl-12 pr-4 py-4 focus:outline-none focus:border-[#3d6a7a] dark:focus:border-[#7ab8c9] focus:ring-2 focus:ring-[#3d6a7a]/10 dark:focus:ring-[#7ab8c9]/10 transition-all placeholder:text-slate-400 dark:placeholder:text-[#3a4a4e]`}
                  />
                </div>
                {errors.email && (
                  <motion.p
                    className="mt-2 text-red-500 dark:text-red-400 text-xs font-['DM_Sans']"
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {errors.email}
                  </motion.p>
                )}
              </div>
            </div>

            {/* Organization & Interest row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Organization field */}
              <div className="group">
                <label
                  htmlFor="organization"
                  className="block text-slate-600 dark:text-[#8a9a9e] text-sm md:text-base mb-2 font-medium"
                >
                  {t('contactForm.form.organization')}
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 dark:text-[#4a5a5e] group-focus-within:text-[#5a8a9a] transition-colors">
                    <Building2 className="w-4 h-4" />
                  </div>
                  <input
                    type="text"
                    id="organization"
                    name="organization"
                    value={formData.organization}
                    onChange={handleChange}
                    placeholder={t('contactForm.form.organizationPlaceholder')}
                    className="w-full bg-white dark:bg-[#0a0f14] border rounded-lg border-slate-200 dark:border-[#1a2530] text-slate-900 dark:text-[#e8ebe9] text-sm md:text-base pl-12 pr-4 py-4 focus:outline-none focus:border-[#3d6a7a] dark:focus:border-[#7ab8c9] focus:ring-2 focus:ring-[#3d6a7a]/10 dark:focus:ring-[#7ab8c9]/10 transition-all placeholder:text-slate-400 dark:placeholder:text-[#3a4a4e]"
                  />
                </div>
              </div>

              {/* Interest field */}
              <div className="group">
                <label
                  htmlFor="interest"
                  className="block text-slate-600 dark:text-[#8a9a9e] text-sm md:text-base mb-2 font-medium"
                >
                  {t('contactForm.form.interest')}
                </label>
                <div className="relative">
                  <select
                    id="interest"
                    name="interest"
                    value={formData.interest}
                    onChange={handleChange}
                    className="w-full bg-white dark:bg-[#0a0f14] border rounded-lg border-slate-200 dark:border-[#1a2530] text-slate-900 dark:text-[#e8ebe9] text-sm md:text-base px-4 py-4 focus:outline-none focus:border-[#3d6a7a] dark:focus:border-[#7ab8c9] focus:ring-2 focus:ring-[#3d6a7a]/10 dark:focus:ring-[#7ab8c9]/10 transition-all appearance-none cursor-pointer"
                  >
                    {interestOptions.map((option) => (
                      <option
                        key={option.value}
                        value={option.value}
                        className="bg-white dark:bg-[#0a0f14]"
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 dark:text-[#4a5a5e] pointer-events-none">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Message field */}
            <div className="group">
              <label
                htmlFor="message"
                className="block text-slate-600 dark:text-[#8a9a9e] text-sm md:text-base mb-2 font-medium"
              >
                {t('contactForm.form.message')} <span className="text-[#3d6a7a] dark:text-[#7ab8c9]">*</span>
              </label>
              <div className="relative">
                <div className="absolute left-4 top-4 text-slate-300 dark:text-[#4a5a5e] group-focus-within:text-[#5a8a9a] transition-colors">
                  <MessageSquare className="w-4 h-4" />
                </div>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t('contactForm.form.messagePlaceholder')}
                  rows={5}
                  className={`w-full bg-white dark:bg-[#0a0f14] border rounded-lg ${errors.message
                    ? 'border-red-500/50'
                    : 'border-slate-200 dark:border-[#1a2530]'
                    } text-slate-900 dark:text-[#e8ebe9] text-sm md:text-base pl-12 pr-4 py-4 focus:outline-none focus:border-[#3d6a7a] dark:focus:border-[#7ab8c9] focus:ring-2 focus:ring-[#3d6a7a]/10 dark:focus:ring-[#7ab8c9]/10 transition-all placeholder:text-slate-400 dark:placeholder:text-[#3a4a4e] resize-none`}
                />
              </div>
              {errors.message && (
                <motion.p
                  className="mt-2 text-red-500 dark:text-red-400 text-xs font-['DM_Sans']"
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {errors.message}
                </motion.p>
              )}
            </div>

            {/* Submit button */}
            <div className="pt-4">
              <motion.button
                type="submit"
                disabled={isSubmitting}
                className="w-full md:w-auto inline-flex items-center justify-center gap-3 px-10 py-4 rounded-lg bg-[#3d6a7a] hover:bg-[#2d5a6a] dark:bg-[#5a8a9a] text-white dark:text-[#0a0f14] text-sm md:text-base font-medium tracking-wide dark:hover:bg-[#7ab8c9] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-md hover:shadow-lg"
                whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('contactForm.form.sending')}
                  </>
                ) : (
                  <>
                    {t('contactForm.form.submit')}
                    <Send className="w-4 h-4" />
                  </>
                )}
              </motion.button>

              {/* Error message */}
              {submitError && (
                <motion.p
                  className="mt-4 text-red-500 dark:text-red-400 text-sm font-medium"
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {submitError}
                </motion.p>
              )}
            </div>
          </div>
        </motion.form>

        {/* Additional contact info */}
        <motion.div
          className="mt-16 pt-12 border-t border-slate-200 dark:border-[#1a2530]"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-slate-100 dark:bg-[#1a2530] flex items-center justify-center">
                <Mail className="w-5 h-5 text-[#3d6a7a] dark:text-[#7ab8c9]" />
              </div>
              <h4 className="text-slate-900 dark:text-[#e8ebe9] text-sm md:text-base mb-1 font-medium">
                {t('contactForm.contactInfo.email')}
              </h4>
              <a
                href="mailto:contact@urbanmaps.io"
                className="text-[#3d6a7a] dark:text-[#7ab8c9] text-sm md:text-base hover:text-[#2d5a6a] dark:hover:text-[#8ac8d9] transition-colors"
              >
                contact@urbanmaps.io
              </a>
            </div>
            <div>
              <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-slate-100 dark:bg-[#1a2530] flex items-center justify-center">
                <Building2 className="w-5 h-5 text-[#3d6a7a] dark:text-[#7ab8c9]" />
              </div>
              <h4 className="text-slate-900 dark:text-[#e8ebe9] text-sm md:text-base mb-1 font-medium">
                {t('contactForm.contactInfo.location')}
              </h4>
              <p className="text-slate-500 dark:text-[#6a7a7e] text-sm md:text-base">
                {t('contactForm.contactInfo.locationValue')}
              </p>
            </div>
            <div>
              <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-slate-100 dark:bg-[#1a2530] flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-[#3d6a7a] dark:text-[#7ab8c9]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h4 className="text-slate-900 dark:text-[#e8ebe9] text-sm md:text-base mb-1 font-medium">
                {t('contactForm.contactInfo.responseTime')}
              </h4>
              <p className="text-slate-500 dark:text-[#6a7a7e] text-sm md:text-base">
                {t('contactForm.contactInfo.responseTimeValue')}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
